/**
 * Builds B-shaped slat segments from an offscreen canvas mask and appends them to an SVG <g>.
 * Geometry matches notes/loading-animations/circle-b-loading-loop.html.
 */

const NS = 'http://www.w3.org/2000/svg';
const CW = 200;
const CH = 200;
const INK_LUM = 148;

export const SLAT_ROWS = [
  [67.28938436837362, 65.42123126325276, 8.9375, 5.875],
  [45.201648063103214, 109.59670387379357, 20.6875, 5.875],
  [31.738668523094276, 136.52266295381145, 32.4375, 5.875],
  [22.281055237477645, 155.4378895250447, 44.1875, 5.875],
  [15.473469401613315, 169.05306119677337, 55.9375, 5.875],
  [10.707730597772354, 178.5845388044553, 67.6875, 5.875],
  [7.6671273326774525, 184.6657453346451, 79.4375, 5.875],
  [6.183773391806042, 187.63245321638792, 91.1875, 5.875],
  [6.183773391806042, 187.63245321638792, 102.9375, 5.875],
  [7.6671273326774525, 184.6657453346451, 114.6875, 5.875],
  [10.707730597772354, 178.5845388044553, 126.4375, 5.875],
  [15.473469401613315, 169.05306119677337, 138.1875, 5.875],
  [22.281055237477645, 155.4378895250447, 149.9375, 5.875],
  [31.738668523094276, 136.52266295381145, 161.6875, 5.875],
  [45.201648063103214, 109.59670387379357, 173.4375, 5.875],
  [67.28938436837362, 65.42123126325276, 185.1875, 5.875],
];

function rawInk(data, px, py) {
  if (px < 0 || px >= CW || py < 0 || py >= CH) return false;
  const i = (py * CW + px) * 4;
  const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  return lum < INK_LUM;
}

function sampleStroke(data, x, yc) {
  let n = 0;
  for (const dy of [-2, -1, 0, 1, 2]) {
    if (rawInk(data, x, yc + dy)) n += 1;
  }
  return n >= 3;
}

function smoothStrokeRuns(stroke, minStrokePx, minGapPx) {
  const lenArr = stroke.length;
  let a = stroke.slice();
  for (let iter = 0; iter < 4; iter += 1) {
    const next = a.slice();
    for (let i = 0; i < lenArr; ) {
      let j = i;
      while (j < lenArr && a[j] === a[i]) j += 1;
      const runLen = j - i;
      if (a[i] && runLen < minStrokePx) {
        for (let k = i; k < j; k += 1) next[k] = false;
      } else if (!a[i] && runLen < minGapPx) {
        for (let k = i; k < j; k += 1) next[k] = true;
      }
      i = j;
    }
    a = next;
  }
  return a;
}

function getLetterBImageData() {
  const canvas = document.createElement('canvas');
  canvas.width = CW;
  canvas.height = CH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, CW, CH);
  ctx.fillStyle = '#000000';
  ctx.font = '900 132px system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('B', 100, 107);
  return ctx.getImageData(0, 0, CW, CH).data;
}

/**
 * @param {SVGGElement} layerGroup
 */
export function appendBSlatSegments(layerGroup) {
  const data = getLetterBImageData();

  SLAT_ROWS.forEach(([L, Wslat, y, h], rowIndex) => {
    const R = L + Wslat;
    const Wfull = R - L;
    const yc = Math.max(2, Math.min(CH - 3, Math.round(y + h / 2)));
    const xStart = Math.floor(L);
    const xEnd = Math.ceil(R);
    const n = xEnd - xStart + 1;
    if (n <= 0 || Wfull < 1) return;

    const stroke = new Array(n);
    for (let i = 0; i < n; i += 1) {
      stroke[i] = sampleStroke(data, xStart + i, yc);
    }
    const smooth = smoothStrokeRuns(stroke, 4, 6);

    let i = 0;
    while (i < n) {
      if (smooth[i]) {
        i += 1;
        continue;
      }
      let j = i;
      while (j < n && !smooth[j]) j += 1;
      const segL = Math.max(L, xStart + i);
      const segR = Math.min(R, xStart + j);
      const segW = segR - segL;
      if (segW < 0.35) {
        i = j;
        continue;
      }

      const cx = segL + segW / 2;
      const cFull = L + Wfull / 2;
      const idleTx = cFull - cx;
      let idleSx = Wfull / segW;
      if (idleSx > 8) idleSx = 8;
      if (idleSx < 1) idleSx = 1;

      const rx = Math.min(2.9375, h / 2, segW / 2);
      const wrap = document.createElementNS(NS, 'g');
      wrap.setAttribute('class', 'about-us-slat-seg-wrap');
      wrap.style.setProperty('--idle-tx', String(idleTx));
      wrap.style.setProperty('--idle-sx', String(idleSx));
      wrap.style.setProperty('--row', String(rowIndex + 1));
      wrap.style.transformOrigin = `${cx}px ${y + h / 2}px`;

      const rect = document.createElementNS(NS, 'rect');
      rect.setAttribute('x', String(segL));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(segW));
      rect.setAttribute('height', String(h));
      rect.setAttribute('rx', String(rx));
      rect.setAttribute('ry', String(rx));
      rect.setAttribute('fill', 'currentColor');
      rect.setAttribute('class', 'about-us-slat-field');

      wrap.appendChild(rect);
      layerGroup.appendChild(wrap);
      i = j;
    }
  });
}
